/**
 * PR A (V204) — medidor de la bolsa anual de folios CFDI (Capa 2) del tenant.
 * Lee GET /me/folios/usage (fn_folio_usage, DEFINER auto-gateada). Dos usos:
 *   - compact: el widget glanceable "Folios usados: N de M (P%) · se renuevan DD/MM/AAAA".
 *   - detallado: el mismo + barra con color por umbral + desglose (emitidas/canceladas).
 * Silencioso: si el fetch falla o no hay dato (403 de no-SUPER_USER, sin ancla), no
 * renderiza nada (feature dormida). Solo lectura: no escribe ni abre periodos.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface FolioUsage {
  periodStart: string;
  periodEnd: string;
  plan: string;
  included: number;
  branchBonus: number;
  includedTotal: number;
  consumed: number;
  emitted: number;
  cancelled: number;
  remaining: number;
  pct: number;
}

/** DATE "YYYY-MM-DD" → "DD/MM/AAAA" sin pasar por new Date (evita el corrimiento -1
 *  bajo TZ=Mexico, [[date-tz-shift-pattern]]). */
function formatDmy(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export function FoliosUsageMeter({ compact = false }: { compact?: boolean }) {
  const [u, setU] = useState<FolioUsage | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api<FolioUsage | null>('/me/folios/usage', { silentError: true })
      .then((d) => { if (alive) setU(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  if (failed || !u) return null;

  const pct = Math.max(0, Math.min(100, u.pct));
  const barColor = u.pct >= 90 ? 'bg-rose-500' : u.pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const renew = formatDmy(u.periodEnd);
  // "Folios usados: N de M (P%) · se renuevan DD/MM/AAAA"
  const headline = (
    <>
      <span className="font-semibold text-slate-800">Folios usados:</span>{' '}
      {u.consumed.toLocaleString('es-MX')} de {u.includedTotal.toLocaleString('es-MX')}{' '}
      ({u.pct}%) · se renuevan {renew}
    </>
  );

  const bar = (
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
    </div>
  );

  if (compact) {
    return (
      <div className="rounded-xl border border-slate-200 p-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Folios de este periodo</p>
        <p className="mt-2 text-sm text-slate-700">{headline}</p>
        {bar}
        <p className="mt-2 text-xs text-slate-500">
          Cada CFDI que emites a tus clientes (al reservar, global mensual o manual) consume un folio.
          Al agotarse, el timbrado <strong>no se bloquea</strong>; recibes avisos al acercarte al límite.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-sm text-slate-700">{headline}</p>
      {bar}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>Emitidas: <strong className="text-slate-700">{u.emitted.toLocaleString('es-MX')}</strong></span>
        <span>Canceladas: <strong className="text-slate-700">{u.cancelled.toLocaleString('es-MX')}</strong></span>
        <span>Restantes: <strong className="text-slate-700">{u.remaining.toLocaleString('es-MX')}</strong></span>
        {u.branchBonus > 0 && (
          <span>Incluye bono por sucursales: <strong className="text-slate-700">+{u.branchBonus.toLocaleString('es-MX')}</strong></span>
        )}
      </div>
    </div>
  );
}
